import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { Optional } from '@/shared/Optional';

const sessionStorageAdapter = createJSONStorage<SessionStorageState>(
    () => sessionStorage,
);

export enum BookingPage {
    SelectPatient = 1,
    SelectPet = 2,
    SelectProduct = 3,
    Disclaimer = 4,
    SelectCategory = 5,
    NotifyOwnDoctor = 6,
    Phone = 7,
    DescribeProblem = 8,
    SelectCaregiver = 9,
    BookOrQueueUp = 10,
}

export enum SessionStorageKeys {
    PreferredCaregiverUid = 'preferredCaregiverUid',
    Category = 'category',
    Phone = 'phone',
    Subject = 'subject',
    Message = 'message',
    OriginalPetWeight = 'originalPetWeight',
    EditedPetWeight = 'editedPetWeight',
    AttachmentUids = 'attachmentUids',
    Timeslot = 'timeslot',
    VisibleDayInTimeSlotSelector = 'visibleDayInTimeSlotSelector',
    Queue = 'queue',
    AiNurseAnswer = 'aiNurseAnswer',
    AiNurseStep = 'aiNurseStep',
    AiNurseQuestion = 'aiNurseQuestion',
    AiNurseSuggestAttachment = 'aiNurseSuggestAttachment',
    IsParentChosen = 'isParentChosen',
}

export type SessionStorageState = Record<SessionStorageKeys, string | null>;

export const bookingErrorAtom = atom<string>('');
export const hasBlockingConsultationAtom = atom<boolean>(false);

export const bookingDraftAtom = atomWithStorage<SessionStorageState>(
    'bookingDraft',
    null,
    sessionStorageAdapter,
);

export const updateDraftAtom = atom(
    null,
    (get, set, update: Partial<SessionStorageState>) => {
        if (Object.keys(update).length > 0) {
            set(bookingDraftAtom, (d) => ({ ...d, ...update }));
        }
    },
);

export const resetDraftAtom = atom(null, (_, set) => {
    set(bookingDraftAtom, null);
});

export const emptyUid = '00000000-0000-0000-0000-000000000000';
export const preferredCaregiverUidAtom = atom(
    (get) => {
        const uid = get(bookingDraftAtom)?.preferredCaregiverUid;
        return uid === emptyUid ? null : uid;
    },
    (_, set, preferredCaregiverUid: Optional<string>) => {
        set(updateDraftAtom, { preferredCaregiverUid });
    },
);

export const bookingDraftByKeyAtom = atomFamily(
    (queryKey: SessionStorageKeys) =>
        atom(
            (get) => get(bookingDraftAtom)?.[queryKey] ?? null,
            (_, set, value: string) => {
                set(updateDraftAtom, { [queryKey]: value });
            },
        ),
);

export const selectedTimeSlotDateAtom = atom(
    (get) => {
        const selectedTimeSlot = get(bookingDraftAtom)?.timeslot;
        return selectedTimeSlot ? new Date(selectedTimeSlot) : null;
    },
    (_, set, value: Optional<Date>) => {
        set(updateDraftAtom, { timeslot: value?.toISOString() });
    },
);

export const visibleDayAtom = bookingDraftByKeyAtom(
    SessionStorageKeys.VisibleDayInTimeSlotSelector,
);

export const phoneNumberAtom = bookingDraftByKeyAtom(SessionStorageKeys.Phone);
